import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'react-toastify';

import { Button } from '../../ui-v2';
import { Table, Badge, EmptyState, Select, Input, Modal } from '../../ui-v2.5';
import {
  getTecnicoCampanas, getParticipantesTecnico, registrarIntervencionTecnico, deshacerIntervencionTecnico,
  getCandidatosTecnico,
} from '../api';
import { ESTADO_PARTICIPACION, etiquetaDe } from '../compartidos/etiquetasEstado';
import RegistrarIntervencionModal from '../compartidos/RegistrarIntervencionModal';
import { mensajeDeError } from '../compartidos/erroresApi';

/**
 * TabParticipantes — panel del técnico (24/10): las personas de una campaña SUYA, para registrar la
 * intervención que hizo por otra vía (presencial, teléfono, remoto, Teams) o marcar su cita, y deshacer lo
 * que registró él.
 *
 * Sólo ve sus campañas (/tecnico/campanas) y el servidor exige ser técnico de ESA campaña: aquí sólo
 * se decide qué se pinta. Registra siempre a su nombre (sin selector de técnico).
 */

const fechaCorta = (s) => (s ? String(s).slice(0, 10).split('-').reverse().join('/') : '—');

export default function TabParticipantes() {
  const [campanas, setCampanas] = useState([]);
  const [campanaId, setCampanaId] = useState(null);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [registrarDe, setRegistrarDe] = useState(null);
  const [deshacerDe, setDeshacerDe] = useState(null);
  const [deshaciendo, setDeshaciendo] = useState(false);
  const [altaAbierta, setAltaAbierta] = useState(false);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const lista = await getTecnicoCampanas();
        if (cancelado) return;
        setCampanas(lista);
        if (lista.length) setCampanaId((prev) => prev ?? lista[0].id);
        else setCargando(false);
      } catch (e) {
        if (!cancelado) {
          setError(mensajeDeError(e, { generico: 'No se pudieron cargar tus campañas.' }).texto);
          setCargando(false);
        }
      }
    })();
    return () => { cancelado = true; };
  }, []);

  const cargar = useCallback(async () => {
    if (!campanaId) return;
    setCargando(true); setError(null);
    try {
      setDatos(await getParticipantesTecnico(campanaId));
    } catch (e) {
      // 403, ruta que el servidor no tiene, o fallo real: cada uno con su mensaje (erroresApi.js).
      setDatos(null);
      setError(mensajeDeError(e, {
        prohibido: 'No eres técnico de esta campaña: sólo puedes registrar intervenciones en las tuyas.',
        generico: 'No se pudieron cargar las personas de la campaña.',
      }).texto);
    } finally {
      setCargando(false);
    }
  }, [campanaId]);
  useEffect(() => { cargar(); }, [cargar]);

  const visibles = useMemo(() => {
    const t = busqueda.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    const lista = datos?.participantes || [];
    if (!t) return lista;
    return lista.filter((p) => String(p.nombre_completo || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').includes(t));
  }, [datos, busqueda]);

  async function confirmarDeshacer() {
    setDeshaciendo(true);
    try {
      await deshacerIntervencionTecnico(campanaId, deshacerDe.persona_id);
      toast.success('Intervención deshecha: vuelve a como estaba.');
      setDeshacerDe(null);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se ha podido deshacer.');
    } finally {
      setDeshaciendo(false);
    }
  }

  const columnas = [
    { key: 'nombre', header: 'Persona', render: (p) => p.nombre_completo },
    { key: 'lote', header: 'Lote', render: (p) => (p.lote ? `Lote ${p.lote.numero}` : '—') },
    {
      key: 'estado', header: 'Estado',
      render: (p) => { const m = etiquetaDe(ESTADO_PARTICIPACION, p.estado); return <Badge tone={m.tone}>{m.label}</Badge>; },
    },
    {
      key: 'cita', header: 'Cita',
      render: (p) => (p.cita_viva ? `${fechaCorta(p.cita_viva.fecha)}${p.cita_viva.hora_inicio ? ` · ${p.cita_viva.hora_inicio}` : ''}` : '—'),
    },
    {
      key: 'acciones', header: '',
      render: (p) => (
        <div className="flex justify-end gap-1">
          {(p.estado === 'pendiente_reserva' || p.estado === 'cita_reservada') && (
            <Button size="sm" variant="outline" onClick={() => setRegistrarDe(p)}>Registrar intervención</Button>
          )}
          {p.puede_deshacer && (
            <Button size="sm" variant="ghost" onClick={() => setDeshacerDe(p)}>Deshacer</Button>
          )}
        </div>
      ),
    },
  ];

  if (!cargando && !error && campanas.length === 0) {
    return (
      <div className="rounded-v25-lg border border-v25-line bg-white">
        <EmptyState variant="minimal" title="No eres técnico de ninguna campaña." />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <Select label="Campaña" value={campanaId || ''} onChange={(e) => setCampanaId(Number(e.target.value))} wrapperClassName="min-w-[260px]">
          {campanas.map((c) => <option key={c.id} value={c.id}>{c.titulo || `Campaña #${c.id}`}</option>)}
        </Select>
        <Input label="Buscar" icon={Search} value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Nombre" wrapperClassName="min-w-[220px]" />
        {cargando && <Loader2 className="mb-2 h-4 w-4 animate-spin text-v25-ink-400" aria-label="Cargando" />}
        {datos && ['Activa', 'Pausada'].includes(datos.campana?.estado) && (
          <Button size="sm" variant="outline" className="ml-auto mb-0.5" onClick={() => setAltaAbierta(true)}>
            Registrar a alguien de fuera
          </Button>
        )}
      </div>

      {error ? (
        <div className="rounded-v25-lg border border-v25-line bg-white">
          <EmptyState variant="medium" title={error}
            action={<Button size="sm" variant="ghost" onClick={cargar}>Reintentar</Button>} />
        </div>
      ) : (
        <Table
          columns={columnas}
          rows={visibles}
          rowKey="persona_id"
          emptyState={<EmptyState variant="minimal" title={busqueda ? 'Nadie con ese nombre.' : 'Esta campaña no tiene personas.'} />}
        />
      )}

      <RegistrarIntervencionModal
        open={!!registrarDe}
        subtareas={datos?.subtareas || []}
        onClose={() => setRegistrarDe(null)}
        persona={registrarDe ? { id: registrarDe.persona_id, nombre: registrarDe.nombre_completo } : null}
        citaViva={registrarDe?.cita_viva || null}
        registrar={(d, pid) => registrarIntervencionTecnico(campanaId, pid, d)}
        onHecho={() => { toast.success('Intervención registrada.'); cargar(); }}
      />
      {/* Alta (decisión del 24/10): el técnico puede añadir a alguien a SU campaña sólo así, al
          registrar la intervención que le hizo, y a su nombre. */}
      <RegistrarIntervencionModal
        open={altaAbierta}
        subtareas={datos?.subtareas || []}
        onClose={() => setAltaAbierta(false)}
        lotes={datos?.lotes || []}
        buscarCandidatos={(q) => getCandidatosTecnico(campanaId, q)}
        registrar={(d, pid) => registrarIntervencionTecnico(campanaId, pid, d)}
        onHecho={() => { toast.success('Añadida a la campaña y con la intervención registrada.'); cargar(); }}
      />
      <Modal
        open={!!deshacerDe}
        onClose={deshaciendo ? undefined : () => setDeshacerDe(null)}
        size="sm"
        tone="danger"
        title="Deshacer la intervención"
        subtitle={deshacerDe?.nombre_completo || ''}
        footer={(
          <>
            <Button variant="ghost" onClick={() => setDeshacerDe(null)} disabled={deshaciendo}>Cancelar</Button>
            <Button variant="destructive" onClick={confirmarDeshacer} disabled={deshaciendo}>
              {deshaciendo ? 'Deshaciendo…' : 'Deshacer'}
            </Button>
          </>
        )}
      >
        <p className="text-sm text-slate-700">
          Vuelve a como estaba antes de registrarla. Queda en el historial quién lo deshizo y cuándo.
        </p>
      </Modal>
    </div>
  );
}
